import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from './Navbar';
import SEO from './SEO';
import RestartCalculator from './RestartCalculator';

// /admin/restart-calc — internal-only tool for figuring out the cost-of-stop
// on a paused or cancelled project. Lightly gated by a `?key=` query param
// matched against REACT_APP_ADMIN_KEY. This is purely obfuscation — anyone
// with the URL+key can use it. Don't store secrets here.

const AdminRestartPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const providedKey = params.get('key') || '';
  const expectedKey = process.env.REACT_APP_ADMIN_KEY || '';

  // If no expected key is configured, the gate is open in dev so the tool is
  // still usable locally without env wiring. In production the env var should
  // always be set.
  const isAuthorized =
    expectedKey === '' || providedKey === expectedKey;

  return (
    <>
      <SEO
        title="Restart fee calculator (admin)"
        description="Internal cost-of-stop tool — not for public use."
        path="/admin/restart-calc"
        noIndex
      />
      <Navbar />
      <main className="bg-[#f2f3d9] min-h-screen">
        <section className="pt-28 sm:pt-32 lg:pt-40 pb-20 sm:pb-24 lg:pb-28">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
            {!isAuthorized ? (
              <div className="rounded-3xl bg-mainkleur text-licht p-8 sm:p-10 lg:p-12 max-w-[640px] mx-auto">
                <span className="font-space text-licht/55 text-xs uppercase tracking-[0.22em]">
                  Restricted area
                </span>
                <h1 className="mt-3 font-bricolage font-extrabold text-licht text-[28px] sm:text-[40px] leading-[0.95] tracking-tight uppercase">
                  Admin only
                </h1>
                <p className="mt-4 font-space text-licht/70 text-base sm:text-lg leading-[1.5]">
                  This page is reserved for internal use. Append <code className="bg-licht/10 px-1.5 py-0.5 rounded text-[#fe9043]">?key=...</code> to the URL with the configured admin key.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 mt-6 text-[#fe9043] hover:text-licht underline underline-offset-4 decoration-2 font-semibold transition-colors duration-300"
                >
                  ← Back to the site
                </Link>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-3">
                  <span className="font-space text-mainkleur/55 text-xs uppercase tracking-[0.22em]">
                    Internal tool · not in nav, not indexed
                  </span>
                  <h1 className="font-anton text-mainkleur text-[14vw] sm:text-[80px] lg:text-[120px] leading-[0.88] tracking-[-3px] sm:tracking-[-5px] lg:tracking-[-7px] uppercase">
                    Restart calc
                  </h1>
                  <p className="font-space text-mainkleur/70 text-base sm:text-lg max-w-[680px]">
                    Plug in the contract total, project stage and event type. The output mirrors the public ToS exactly — copy the Markdown breakdown straight into an invoice or email reply.
                  </p>
                </div>
                <RestartCalculator variant="admin" />
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default AdminRestartPage;
